/**
 * The accounts §18.5 dropped to buy a usable window, named individually and
 * in §6.5's shed order, with the two levers beside them.
 *
 * Renders only when the funded window is loaded and has a non-empty shed
 * set. A zero balance sheds nothing (there is no window to make usable), an
 * absent window has nothing confirmed to report, and a fully-funded window
 * has nothing shed either -- all three render nothing here, same as
 * `MissedSchedule`.
 */

import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { DataTable } from '../../../chrome/table';
import { EmptyState } from '../../../chrome/state';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import type { AccountRef } from '../api/schemas';
import { tt } from '../i18n';
import { bm } from './messages';
import { OrgAdminOnly } from '../capacity/NoOpenRequest';
import { shedAccounts } from './state';
import { useFundedWindow } from './useFundedWindow';

const COLUMNS: ColumnDef<AccountRef, unknown>[] = [
  { accessorKey: 'handle', header: bm('shed.handle') },
  {
    accessorKey: 'platform',
    header: bm('shed.platform'),
    cell: (info) => <Badge variant="secondary">{info.getValue<AccountRef['platform']>()}</Badge>,
  },
  {
    accessorKey: 'credits_per_hour',
    header: bm('shed.rate'),
    meta: { numeric: true },
    // `shedAccounts` lists priced accounts only.
    cell: (info) => integer(info.getValue<number>(), getLocale()),
  },
];

export function ShedAccounts(props: {
  tenantId: string;
  /** Where the "track fewer accounts" lever points, per campaign. The
   *  tracked-account list and its path belong to another area, so the
   *  caller supplies the mapping and this module imports nothing of that
   *  area's and hardcodes nothing of its own. */
  accountsPathFor: (campaignId: string) => string;
}): ReactNode {
  const { data: fundedWindow } = useFundedWindow(props.tenantId);

  if (fundedWindow === undefined) return null;

  const shed = shedAccounts(fundedWindow);
  const [firstShed] = shed;
  // Prevents rendering when shed_accounts is empty. When the array is empty,
  // `firstShed` is undefined and accessing its properties would crash;
  // this guard is also required for `noUncheckedIndexedAccess` narrowing.
  if (firstShed === undefined) return null;

  return (
    <div data-testid="shed-accounts">
      <p>{tt('shed.title')}</p>
      <p>{tt('shed.why')}</p>
      <DataTable<AccountRef>
        columns={COLUMNS}
        data={shed}
        getRowId={(row) => row.id}
        rowHref={(row) => props.accountsPathFor(row.campaign_id)}
        caption={tt('shed.title')}
        emptyState={<EmptyState title={tt('shed.title')} />}
      />
      <Button asChild size="sm" variant="outline">
        <Link to={props.accountsPathFor(firstShed.campaign_id)}>{tt('shed.lever.fewer')}</Link>
      </Button>
      <OrgAdminOnly tenantId={props.tenantId}>
        <Button asChild size="sm" variant="outline">
          <Link to="/t/$tenantId/creditos" params={{ tenantId: props.tenantId }}>
            {tt('shed.lever.credits')}
          </Link>
        </Button>
      </OrgAdminOnly>
    </div>
  );
}
